import { createPromise, isReactivePromise } from './async.js';
import { getCurrentConsumer, setCurrentConsumer } from './consumer.js';
import { createEdge } from './edge.js';
import { generatorResultToPromiseWithConsumer } from './generators.js';
import { isRelay } from './reactive.js';
import { scheduleListeners, scheduleTracer } from './scheduling.js';
import { getTracerProxy, SignalType, TracerEventType } from './trace.js';
import { isGeneratorResult, isPromise } from './utils/type-utils.js';
import { unwatchSignal, watchSignal } from './watch.js';
export function getSignal(signal) {
    const currentConsumer = getCurrentConsumer();
    if (currentConsumer !== undefined) {
        const { ref, computedCount, deps } = currentConsumer;
        const prevEdge = deps.get(signal);
        const prevConsumedAt = prevEdge?.consumedAt;
        if (prevConsumedAt !== computedCount) {
            if (prevEdge === undefined) {
                if (IS_DEV) {
                    getTracerProxy()?.emit({
                        type: TracerEventType.Connected,
                        id: currentConsumer.tracerMeta.id,
                        childId: signal.tracerMeta.id,
                        name: signal.tracerMeta.desc,
                        params: signal.tracerMeta.params,
                        nodeType: SignalType.Reactive,
                    });
                }
                if (currentConsumer.watchCount > 0) {
                    watchSignal(signal);
                }
            }
            const updatedAt = checkSignal(signal);
            const newEdge = createEdge(prevEdge, 0 /* EdgeType.Signal */, signal, updatedAt, computedCount);
            signal.subs.set(ref, newEdge);
            deps.set(signal, newEdge);
        }
        else {
            const updatedAt = checkSignal(signal);
            if (prevEdge !== undefined) {
                prevEdge.updatedAt = updatedAt;
            }
        }
    }
    else {
        checkSignal(signal);
    }
    return signal._value;
}
export function checkSignal(signal) {
    const { ref, _state: state } = signal;
    if (state < 2 /* ReactiveFnState.Dirty */) {
        return signal.updatedCount;
    }
    if (state >= 3 /* ReactiveFnState.MaybeDirty */) {
        let edge = signal.dirtyHead;
        while (edge !== undefined) {
            if (edge.type === 1 /* EdgeType.Promise */) {
                const dep = edge.dep;
                // If the dependency is pending, then we need to propagate the pending state to the
                // parent signal, and we halt the computation here.
                if (dep._getPending()) {
                    const value = signal._value;
                    // Add the signal to the awaitSubs map to be notified when the promise is resolved
                    dep['_awaitSubs'].set(ref, edge);
                    // Propagate the pending state to the parent signal
                    value._setPending();
                    signal._state = 1 /* ReactiveFnState.Pending */;
                    signal.dirtyHead = edge;
                    // Early return to prevent the signal from being computed and to preserve the dirty state
                    return signal.updatedCount;
                }
                else if (edge.updatedAt === edge.dep._updatedCount) {
                    // Add the signal to the awaitSubs map as its still a dependency, just not dirty
                    dep['_awaitSubs'].set(ref, edge);
                }
                else {
                    signal.dirtyHead = edge.nextDirty;
                    signal._state = 2 /* ReactiveFnState.Dirty */;
                    break;
                }
                edge = edge.nextDirty;
                continue;
            }
            const dep = edge.dep;
            const updatedAt = checkSignal(dep);
            dep.subs.set(ref, edge);
            if (edge.updatedAt !== updatedAt) {
                signal.dirtyHead = edge.nextDirty;
                signal._state = 2 /* ReactiveFnState.Dirty */;
                break;
            }
            edge = edge.nextDirty;
        }
    }
    const newState = signal._state;
    // If the signal is dirty, we need to run it. This should always be checked
    // directly on the signal instance, because the state could have been changed
    // mid computation and not just through direct dependencies.
    if (newState === 2 /* ReactiveFnState.Dirty */) {
        if (signal._isLazy) {
            signal.updatedCount++;
        }
        else {
            runSignal(signal);
        }
    }
    else if (newState === 4 /* ReactiveFnState.PendingDirty */) {
        signal._value._clearPending();
    }
    signal._state = 0 /* ReactiveFnState.Clean */;
    signal.dirtyHead = undefined;
    if (IS_DEV && getTracerProxy() !== undefined && signal.tracerMeta?.tracer) {
        scheduleTracer(signal.tracerMeta.tracer);
    }
    return signal.updatedCount;
}
export function runSignal(signal) {
    let tracer;
    if (IS_DEV) {
        tracer = getTracerProxy();
        tracer?.emit({
            type: TracerEventType.StartUpdate,
            id: signal.tracerMeta.id,
        });
    }
    const prevConsumer = getCurrentConsumer();
    const updatedCount = signal.updatedCount;
    const computedCount = ++signal.computedCount;
    try {
        setCurrentConsumer(signal);
        const initialized = updatedCount !== 0;
        const prevValue = signal._value;
        let nextValue = signal.def.compute(...signal.args);
        let valueIsPromise = false;
        if (nextValue !== null && typeof nextValue === 'object') {
            if (isGeneratorResult(nextValue)) {
                nextValue = generatorResultToPromiseWithConsumer(nextValue, signal);
                valueIsPromise = true;
            }
            else if (isPromise(nextValue)) {
                valueIsPromise = true;
            }
        }
        if (valueIsPromise) {
            if (prevValue !== null && typeof prevValue === 'object' && isReactivePromise(prevValue)) {
                // Update the AsyncSignal with the new promise. Since the value
                // returned from the function is the same AsyncSignal instance,
                // we don't need to increment the updatedCount, because the returned
                // value is the same. _setPromise will update the nested values on the
                // AsyncSignal instance, and consumers of those values will be notified
                // of the change through that.
                prevValue['_setPromise'](nextValue);
            }
            else {
                signal._value = createPromise(nextValue, signal);
                signal.updatedCount = updatedCount + 1;
            }
        }
        else {
            if (!initialized || !signal.def.equals(prevValue, nextValue)) {
                signal._value = nextValue;
                // If the signal is lazy, we don't want to increment the updatedCount, it
                // has already been updated
                signal.updatedCount = signal._isLazy ? updatedCount : updatedCount + 1;
            }
            // Disconnect the signal from all its previous dependencies synchronously
            disconnectSignal(signal, computedCount);
        }
    }
    finally {
        setCurrentConsumer(prevConsumer);
        if (IS_DEV) {
            tracer?.emit({
                type: TracerEventType.EndUpdate,
                id: signal.tracerMeta.id,
                value: isRelay(signal) ? '...' : signal._value,
            });
        }
    }
}
export function disconnectSignal(signal, computedCount = signal.computedCount) {
    const { ref, deps } = signal;
    for (const [dep, edge] of deps) {
        if (edge.consumedAt !== computedCount) {
            unwatchSignal(dep);
            dep.subs.delete(ref);
            deps.delete(dep);
            // Mark disconnected dep as Dirty so it revalidates when re-read.
            // Its subscription chain is broken and it may miss notifications.
            if (dep._state < 2 /* ReactiveFnState.Dirty */) {
                dep._state = 2 /* ReactiveFnState.Dirty */;
            }
        }
    }
}
export function checkAndRunListeners(signal) {
    const listeners = signal.listeners;
    let updatedCount = checkSignal(signal);
    if (listeners !== null && listeners.updatedAt !== updatedCount) {
        listeners.updatedAt = updatedCount;
        scheduleListeners(signal);
    }
    return updatedCount;
}
